import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
} from '@mui/material';
import styled from 'styled-components';
import EllipsisText from 'components/EllipsisText';
import { CsmSelector } from 'CsmModule/components/CsmSelector';
import { ComponentProps, useEffect, useRef } from 'react';
import { chipWidth } from '@constants';
import { useArrayTagsInput } from 'hooks/useArrayItems/useArrayTagsInput';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { updateTenant } from 'thunk';
import { useDispatch } from 'react-redux';
import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';
import { useCsmSelector } from 'CsmModule/hooks/useCsmSelector';
import { csmHashById, csmList } from 'CsmModule/constants';

const TagContainer = styled.div`
  display: flex;
  .add-tag-div {
    flex: 2;
    min-width: 80px;
  }
  .initial-tag-div {
    flex: 2;
  }
  .start-date {
    flex: 2;
  }
`;

const ChipsContainer = styled.div`
  margin-bottom: 15px;
  .chip {
    margin-right: 5px;
    margin-bottom: 5px;
    max-width: 370px;
    height: 27px;
  }
  .red-chip {
    background-color: #f44336;
    color: white;
  }
`;

export type EditTenantModalProps = {
  onClose: VoidFunction;
  tenantId: number;
  onEditTenantSuccess?: VoidFunction;
};

const requestStatusId = 'edit-tenant-modal';

export const EditTenantModal = ({
  onClose,
  tenantId,
  onEditTenantSuccess,
}: EditTenantModalProps) => {
  const dispatch = useDispatch();
  const { getTenant } = useTenants();
  const tenant = getTenant({ tenantId: tenantId });
  // Seed the selector with the tenant's assigned CSM. The CSM list is keyed by the
  // CSM's ACCOUNT id — the tenant id previously passed here is from an unrelated id
  // space, so the prefill was garbage whenever the ids happened to collide.
  const currentCsmAccountId = csmList.find((csm) => csm.name === tenant?.csm)?.accountId ?? null;
  const { onCsmSelectorClick, selectedCsm, setCsm } = useCsmSelector({
    accountId: currentCsmAccountId,
  });
  // useCsmSelector only seeds its state on first render; when the tenant arrives
  // asynchronously, sync the derived CSM in — but never over a choice the user
  // already made in the dropdown.
  useEffect(() => {
    // The !selectedCsm guard means a user's own dropdown choice is never overwritten
    // when this re-runs.
    if (currentCsmAccountId && !selectedCsm) {
      setCsm(csmHashById[currentCsmAccountId]);
    }
  }, [currentCsmAccountId, selectedCsm, setCsm]);
  const { requestStatus } = useRequestStatus({
    requestStatusId,
  });
  const isRequesting = Boolean(requestStatus?.isRequesting);

  const { addTag, removeTag, inputRef, tagsList, tagError, setTagsList } = useArrayTagsInput();

  // Prefill with the tenant's existing tags — an empty list here meant "Save"
  // silently WIPED every tag the tenant already had. Never over user edits: if the
  // user touched the tag list before the tenant resolved asynchronously, their
  // in-progress edits win. A different tenant opening the modal re-seeds.
  const tagsEditedRef = useRef(false);
  const prevTenantIdRef = useRef(tenantId);
  useEffect(() => {
    // Reset the dirty flag when the modal is retargeted at a DIFFERENT tenant —
    // keyed on the stable tenantId PROP, not the async tenant?.id: a transient
    // undefined from getTenant mid-refetch must neither reset the flag (letting
    // the seed effect wipe in-progress edits) nor leave a stale flag that carries
    // one tenant's edits over to another.
    if (prevTenantIdRef.current !== tenantId) {
      prevTenantIdRef.current = tenantId;
      tagsEditedRef.current = false;
    }
  }, [tenantId]);
  useEffect(() => {
    if (!tagsEditedRef.current) {
      setTagsList(tenant?.tags ?? []);
    }
    // Keyed on the tenant identity, NOT tenant?.tags — re-seeding on tags
    // reference changes would overwrite in-progress edits (the dirty flag guards
    // the async-arrival case), so the missing-dep suppression is deliberate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id, setTagsList]);

  const onAddTag: typeof addTag = (e) => {
    tagsEditedRef.current = true;
    addTag(e);
  };

  const onDelete = (tag: string) => {
    tagsEditedRef.current = true;
    removeTag(tag);
  };

  const handleSubmitEditTenantForm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (tenant) {
      const params: Parameters<typeof updateTenant>[0] = {
        tenantName: tenant.tenantName,
        csm: selectedCsm?.name,
        tags: tagsList,
        meta: {
          onSuccess: () => {
            onClose();
            onEditTenantSuccess?.();
          },
        },
      };
      // TODO: Fix with Redux Toolkit configureStore migration
      dispatch(updateTenant(params) as any);
    }
  };

  const handleCsmSelectorClick: ComponentProps<typeof CsmSelector>['onClick'] = (e) => {
    onCsmSelectorClick({ target: e.currentTarget });
  };

  return (
    <Dialog maxWidth={'xs'} fullWidth open onClose={onClose}>
      <DialogTitle id="form-dialog-title">Edit Tenant: {tenant?.['tenantName']}</DialogTitle>
      <DialogContent>
        <CsmSelector
          accountId={selectedCsm?.accountId || null}
          onClick={handleCsmSelectorClick}
          tooltip="Select Customer Success Manager for this tenant"
        />
        <FormControl component="fieldset" fullWidth>
          <Box component="h3">Edit tags</Box>
          <TagContainer>
            <div>
              <TextField
                placeholder="Enter custom tag"
                inputRef={inputRef}
                onKeyDown={onAddTag}
                helperText={tagError}
                error={!!tagError}
                disabled={isRequesting}
              />
              <ChipsContainer>
                {tagsList?.map((tag: string) => (
                  <EllipsisText key={tag} text={tag} width={chipWidth}>
                    <Chip
                      className="chip"
                      color="primary"
                      label={tag}
                      onDelete={isRequesting ? undefined : () => onDelete(tag)}
                      style={{ maxWidth: `calc(${chipWidth} - 10%)` }}
                    />
                  </EllipsisText>
                ))}
              </ChipsContainer>
            </div>
          </TagContainer>
        </FormControl>

        <DialogActions>
          <Button color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!!tagError} color="primary" onClick={handleSubmitEditTenantForm}>
            Save
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
